import { http } from "@dashboard/common/http";
import { ImageProductForm } from "@dashboard/modules/products/types/product-form.types";

const authHeaders = (token: string) =>
  ({
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  }) as { [key: string]: string };

const parseLensResponse = (data: unknown): string[] => {
  if (!data || typeof data !== "object") return [];
  const list = (data as Record<string, unknown>)["google-lens"];
  if (!Array.isArray(list)) return [];
  return list.filter((item): item is string => typeof item === "string" && !!item);
};

export const fetchRelatedImagesFromUrl = async (
  token: string,
  imageUrl: string
): Promise<string[]> => {
  const { data, status } = await http.post(
    "/api/ms/google-lens/",
    { product_image_link: imageUrl },
    { headers: { ...authHeaders(token), "Content-Type": "application/json" } }
  );
  if (status !== 200) return [];
  return parseLensResponse(data);
};

export const fetchRelatedImagesFromFile = async (
  token: string,
  file: File
): Promise<string[]> => {
  const formData = new FormData();
  formData.append("image", file);
  const { data, status } = await http.post(
    "/api/ms/google-lens/from-file",
    formData,
    {
      headers: authHeaders(token),
    }
  );
  if (status !== 200) return [];
  return parseLensResponse(data);
};

export const fetchRelatedImagesForFormImage = async (
  token: string,
  image: ImageProductForm
): Promise<string[]> => {
  if (image.type === "url" && typeof image.image === "string") {
    return fetchRelatedImagesFromUrl(token, image.image);
  }
  if (image.type === "file" && image.image instanceof File) {
    return fetchRelatedImagesFromFile(token, image.image);
  }
  return [];
};

export const urlToImageFile = async (
  url: string,
  index: number
): Promise<File> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("download_failed");
  }
  const blob = await response.blob();
  const extension =
    blob.type.split("/")[1]?.split(";")[0] ||
    url.split(".").pop()?.split("?")[0] ||
    "jpg";
  return new File([blob], `photo-ai-${Date.now()}-${index}.${extension}`, {
    type: blob.type || "image/jpeg",
  });
};

export const urlsToImageFormItems = async (
  urls: string[],
  startId: number
): Promise<ImageProductForm[]> => {
  const files = await Promise.all(
    urls.map((url, index) => urlToImageFile(url, index))
  );
  return files.map((file, index) => ({
    id: startId + index,
    type: "file" as const,
    image: file,
  }));
};
